import React, { useState, useEffect } from 'react';
import { Music2, AlignJustify as Spotify, X } from 'lucide-react';
import { connectSpotify } from '../lib/spotify';
import { connectAppleMusic } from '../lib/appleMusic';
import { useUser } from '../contexts/UserContext';

interface MusicServicesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

function MusicServicesDialog({ isOpen, onClose }: MusicServicesDialogProps) {
  const { user, updateProfile } = useUser();
  const [connecting, setConnecting] = useState<'spotify' | 'apple' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState({
    spotify: false,
    apple: false
  });

  // Update connection status when user data changes
  useEffect(() => {
    if (user) {
      setConnectionStatus({
        spotify: user.spotifyConnected || false,
        apple: user.appleMusicConnected || false
      });
    }
  }, [user]);

  const handleSpotifyConnect = async () => {
    if (connecting === 'spotify') return;
    
    try {
      setError(null);
      setConnecting('spotify');
      await connectSpotify();
      // The page will redirect to Spotify, so we don't need to handle success here
    } catch (error) {
      console.error('Error connecting to Spotify:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect to Spotify. Please try again.');
    } finally {
      setConnecting(null);
    }
  };

  const handleAppleMusicConnect = async () => {
    if (connecting === 'apple') return;

    try {
      setError(null);
      setConnecting('apple');
      
      await connectAppleMusic();
      
      // Update local state
      if (user) {
        await updateProfile({
          ...user,
          appleMusicConnected: true,
        });
        
        setConnectionStatus(prev => ({
          ...prev,
          apple: true
        }));
      }

      // Close dialog on success
      onClose();
    } catch (error) {
      console.error('Error connecting to Apple Music:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect to Apple Music. Please try again.');
    } finally {
      setConnecting(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Connect Music Services</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <button 
            onClick={handleSpotifyConnect}
            disabled={connecting === 'spotify'}
            className={`w-full py-3 px-4 rounded-lg flex items-center justify-between transition-colors ${
              connectionStatus.spotify
                ? 'bg-green-100 text-green-700'
                : 'bg-green-500 text-white hover:bg-green-600'
            } ${connecting === 'spotify' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center gap-3">
              <Spotify className="h-5 w-5" />
              <span>
                {connectionStatus.spotify ? 'Connected to Spotify' : 'Connect Spotify'}
              </span>
            </div>
            {connecting === 'spotify' && (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            )}
          </button>

          <button
            onClick={handleAppleMusicConnect}
            disabled={connecting === 'apple'}
            className={`w-full py-3 px-4 rounded-lg flex items-center justify-between transition-colors ${
              connectionStatus.apple
                ? 'bg-pink-100 text-pink-700'
                : 'bg-pink-500 text-white hover:bg-pink-600'
            } ${connecting === 'apple' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center gap-3">
              <Music2 className="h-5 w-5" />
              <span>
                {connectionStatus.apple ? 'Connected to Apple Music' : 'Connect Apple Music'}
              </span>
            </div>
            {connecting === 'apple' && (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            )}
          </button>

          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="text-sm text-gray-600 mt-4">
            <h4 className="font-medium mb-2">Connection Status:</h4>
            <ul className="space-y-1">
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${connectionStatus.spotify ? 'bg-green-500' : 'bg-gray-300'}`} />
                Spotify: {connectionStatus.spotify ? 'Connected' : 'Not connected'}
              </li>
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${connectionStatus.apple ? 'bg-green-500' : 'bg-gray-300'}`} />
                Apple Music: {connectionStatus.apple ? 'Connected' : 'Not connected'}
              </li>
            </ul>
          </div>

          <p className="text-sm text-gray-600 mt-4">
            Connect your music services to compare playlists, share music, and discover new tracks with friends.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MusicServicesDialog;